// Shared leave-note fields + submit helper for offline configure modal and event transition dialogs.

import React, { JSX, useState } from 'react';
import InlineSpinner from './InlineSpinner';
import {
  dispatchToast,
  formatApiResponseError,
} from '../EventsManagementView/utils';
import type { DeviceNoteType, AddDeviceNotesRequestResult } from './types/DeviceNoteTypes';

export const DEVICE_NOTE_MAX_LENGTH = 1000;

/** Validates and POSTs append-only device notes (same contract as Devices offline op). */
export async function addDeviceNotesRequest(params: {
  deviceIds: string[];
  type: DeviceNoteType;
  message: string;
  eventId?: string;
}): Promise<AddDeviceNotesRequestResult> {
  if (!window.eventsAPI?.addDeviceNotes) {
    return { ok: false, errorMessage: 'Device notes are not available' };
  }
  const ids = params.deviceIds.filter((id) => String(id || '').trim());
  if (ids.length === 0) {
    return { ok: false, errorMessage: 'No devices selected' };
  }
  const trimmed = String(params.message || '').trim();
  if (!trimmed) {
    return { ok: false, errorMessage: 'Note message is required' };
  }
  if (trimmed.length > DEVICE_NOTE_MAX_LENGTH) {
    return {
      ok: false,
      errorMessage: `Note is too long (max ${DEVICE_NOTE_MAX_LENGTH} characters)`,
    };
  }
  const eventId = String(params.eventId || '').trim();
  const res = await window.eventsAPI.addDeviceNotes({
    deviceIds: ids,
    type: params.type,
    message: trimmed,
    eventId: eventId || undefined,
  });
  if (!res?.ok) {
    return {
      ok: false,
      errorMessage: `Failed to save note: ${formatApiResponseError(res)}`,
    };
  }
  return { ok: true };
}

import type { LeaveDeviceNoteFormProps } from './types/SharedProps';

export type { LeaveDeviceNoteFormProps };

export function LeaveDeviceNoteForm(props: LeaveDeviceNoteFormProps): JSX.Element {
  const {
    deviceIds,
    noteType,
    onNoteTypeChange,
    message,
    onMessageChange,
    disabled = false,
    visualVariant,
    title = 'Leave note',
    eventAssociation,
    eventContextHint,
    showDeviceList = true,
    deviceListTitle,
    deviceListMaxHeightClass,
    placeholder,
    footerHelpText = 'Saved with timestamp and user (auto-filled server-side).',
    footer,
    submitEventId = '',
    onInlineCancel,
    onInlineSaved,
  } = props;

  const [saving, setSaving] = useState(false);
  const trimmed = message.trim();
  const chars = trimmed.length;

  const isConfigure = visualVariant === 'configure';
  const outerClass = isConfigure
    ? title
      ? 'border border-gray-100 dark:border-slate-700 rounded-lg p-3'
      : ''
    : 'rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-sm ring-1 ring-gray-200 dark:ring-slate-700';
  const titleClass = isConfigure ? 'text-xs font-semibold text-gray-900 dark:text-slate-100' : 'text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-slate-300';
  const labelClass = isConfigure ? 'block text-[11px] text-gray-700 dark:text-slate-300 mb-1' : 'block text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400';
  const selectClass = isConfigure
    ? 'w-full rounded-md border border-gray-300 dark:border-slate-600 px-2 py-1 text-[11px] bg-white dark:bg-slate-900 disabled:opacity-60'
    : 'mt-1 w-full rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs text-gray-900 dark:text-slate-100 shadow-sm focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-blue-500/30';
  const textareaClass = isConfigure
    ? 'w-full min-h-[120px] rounded-md border border-gray-300 dark:border-slate-600 px-2 py-1 text-[11px] bg-white dark:bg-slate-900 disabled:opacity-60'
    : 'mt-1 w-full min-h-[88px] resize-y rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-2 text-xs text-gray-900 dark:text-slate-100 shadow-sm focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-blue-500/30';
  const listBoxClass = isConfigure
    ? 'border border-gray-100 rounded-md p-2 bg-gray-50 dark:bg-slate-800'
    : 'mt-2 max-h-16 overflow-y-auto rounded-md border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-2 py-1.5';

  async function handleInlineSave(): Promise<void> {
    setSaving(true);
    try {
      const result = await addDeviceNotesRequest({
        deviceIds,
        type: noteType,
        message,
        eventId: submitEventId,
      });
      if (!result.ok) {
        dispatchToast('toast-error', result.errorMessage);
        return;
      }
      dispatchToast(
        'toast-info',
        `Saved note for ${deviceIds.length} device${deviceIds.length === 1 ? '' : 's'}`,
      );
      onMessageChange('');
      onInlineSaved?.();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={outerClass}>
      <div className={isConfigure ? 'flex items-center justify-between mb-2' : 'flex flex-wrap items-start justify-between gap-2'}>
        {title ? <h3 className={titleClass}>{title}</h3> : <span />}
        {footer === 'inline' && onInlineCancel ? (
          <button
            type="button"
            className={
              isConfigure
                ? 'text-[11px] text-gray-600 hover:text-gray-800 dark:text-slate-200'
                : 'shrink-0 rounded-md px-2 py-1 text-[11px] font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800'
            }
            onClick={onInlineCancel}
            disabled={saving || disabled}
          >
            Close
          </button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-3">
        {eventAssociation ? <div>{eventAssociation}</div> : null}

        {eventContextHint && !eventAssociation ? (
          <div className={isConfigure ? 'text-[11px] text-gray-600 dark:text-slate-400' : 'text-[11px] text-gray-500 dark:text-slate-400 sm:flex sm:items-end'}>
            {eventContextHint}
          </div>
        ) : null}

        <div>
          <label className={isConfigure ? 'mt-1 block text-[11px] font-semibold text-gray-700 dark:text-slate-300' : labelClass}>
            {isConfigure ? 'Note' : 'Type'}
          </label>
          {isConfigure ? (
            <p className="mb-2 text-[10px] text-gray-500 dark:text-slate-400">
              Type and message are saved to the device notes log.
            </p>
          ) : null}
          {!isConfigure ? null : (
            <label className={`${labelClass} mt-2`}>Type</label>
          )}
          <select
            className={selectClass}
            value={noteType}
            onChange={(e) => onNoteTypeChange(e.target.value as DeviceNoteType)}
            disabled={saving || disabled}
          >
            <option value="info">info</option>
            <option value="warning">warning</option>
            <option value="error">error</option>
            <option value="debug">debug</option>
          </select>
        </div>

        <div>
          <label className={labelClass}>{isConfigure ? 'Message' : 'Message'}</label>
          <textarea
            className={textareaClass}
            value={message}
            maxLength={DEVICE_NOTE_MAX_LENGTH}
            onChange={(e) => onMessageChange(e.target.value)}
            placeholder={placeholder ?? 'Write a note about these devices…'}
            disabled={saving || disabled}
          />
          <div className={isConfigure ? 'mt-1 text-[11px] text-gray-500 dark:text-slate-400' : 'mt-0.5 text-right text-[10px] text-gray-500 dark:text-slate-400'}>
            {chars}/{DEVICE_NOTE_MAX_LENGTH}
          </div>
        </div>

        {showDeviceList ? (
          isConfigure ? (
            <div className={listBoxClass}>
              <div className="text-[11px] text-gray-800 dark:text-slate-200 font-medium mb-1">
                Selected devices ({deviceIds.length})
              </div>
              <div className={`max-h-28 overflow-auto ${deviceListMaxHeightClass ?? ''}`}>
                {deviceIds.length > 0 ? (
                  deviceIds.map((id) => (
                    <div key={id} className="font-mono text-[11px] break-all text-gray-800 dark:text-slate-200">
                      {id}
                    </div>
                  ))
                ) : (
                  <div className="text-[11px] text-gray-400 dark:text-slate-400">No devices selected.</div>
                )}
              </div>
              <div className="mt-2 text-[11px] text-gray-500 dark:text-slate-400">{footerHelpText}</div>
            </div>
          ) : (
            <div className={listBoxClass}>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                {deviceListTitle ?? 'Device IDs'}
              </div>
              <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 font-mono text-[11px] text-gray-800 dark:text-slate-200">
                {deviceIds.length > 0 ? (
                  deviceIds.map((id) => (
                    <span key={id} className="break-all">
                      {id}
                    </span>
                  ))
                ) : (
                  <span className="text-[11px] text-gray-500 dark:text-slate-400">No devices.</span>
                )}
              </div>
              <div className="mt-2 text-[10px] text-gray-500 dark:text-slate-400">{footerHelpText}</div>
            </div>
          )
        ) : null}
      </div>

      {footer === 'inline' ? (
        <div className="mt-3 flex flex-wrap justify-end gap-2">
          {onInlineCancel ? (
            <button
              type="button"
              className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs font-medium text-gray-700 dark:text-slate-300 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50"
              onClick={onInlineCancel}
              disabled={saving || disabled}
            >
              Cancel
            </button>
          ) : null}
          <button
            type="button"
            className="inline-flex min-h-[2rem] items-center justify-center rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
            onClick={() => void handleInlineSave()}
            disabled={saving || disabled || !trimmed}
          >
            {saving ? <InlineSpinner /> : 'Save note'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

